(function () {
  'use strict';

  /**
   * @typedef {{src: string, alt: string, width: number, height: number}} FeaturedMedia
   * @typedef {{id: number, title: string, available: boolean, price: number, compareAtPrice: number, options: string[], featuredMedia: FeaturedMedia|null}} VariantPayload
   * @typedef {{name: string, value?: string, dv?: string}} NutritionRow
   * @typedef {{label?: string, content?: string, shippingNote?: string, insideCopy?: string, unitCount?: number|string}} PackOption
   * @typedef {{id: string, url: string, name: string, isWholesale: boolean, srp: number|null, image: string, imageWidth: number, imageHeight: number, images: string[], price: number, compareAtPrice: number, selectedVariantId: number, variants: VariantPayload[], packOptions: PackOption[], tags: string[], badge: string, description: string, pdpDescription: string, facts: string[], nutrition: NutritionRow[], magic: Record<string, unknown>, shippingNote: string, shipping: string}} ProductPayload
   * @typedef {Record<string, string>} LocalizedStrings
   * @typedef {{products: ProductPayload[], strings: LocalizedStrings, routes: {root: string, cart: string, checkout: string, retailCatalog: string, wholesaleCatalog: string}, catalogMode: {enabled: boolean, email: string, buttonText: string, subjectTemplate: string, bodyTemplate: string}, purchaseOptions: Array<{id: string, label: string, badge?: string, discount: number}>, subscriptionDetail?: {summary: string, body: string, frequencies: string[]}, giftCardPromo: {threshold: number, headline: string, shortLabel: string}, getProductById: (id: string) => ProductPayload|null, addToCart: Function, getCart: Function, updateCartCount: Function, money: (value: number) => string}} StorefrontConfig
   */
  /** @type {StorefrontConfig} */
  const site = window.DELI_DELE_SITE;
  if (!site || !Array.isArray(site.products) || !site.routes || typeof site.getProductById !== 'function') {
    console.error('Deli Delé storefront configuration is invalid.');
    return;
  }
  /** @type {LocalizedStrings} */
  const strings = site?.strings || {};
  const isVariantPayload = variant => variant
    && Number.isFinite(Number(variant.id))
    && typeof variant.title === 'string'
    && Number.isFinite(Number(variant.price));
  const isProductPayload = product => product
    && typeof product.id === 'string'
    && typeof product.url === 'string'
    && typeof product.name === 'string'
    && Array.isArray(product.variants)
    && product.variants.every(isVariantPayload);
  /** @type {ProductPayload[]} */
  const PRODUCTS = site.products.filter(isProductPayload);
  if (PRODUCTS.length !== site.products.length) {
    console.error('One or more Shopify product payloads were rejected as invalid.');
  }
  const WHOLESALE_DISPLAY_PRICING = Object.freeze({
    default: Object.freeze({ perBag: 1.99 }),
    pureCrunch: Object.freeze({ handle: 'ws-pure-crunch', perBag: 2.09 })
  });
  const WHOLESALE_CASE_SIZE = 12;
  let toastTimer;

  function interpolate(template, values) {
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replaceAll(`[${key}]`, String(value)),
      String(template || '')
    );
  }

  /**
   * Calculate a sale percentage from Shopify price data.
   * @param {number|string} price
   * @param {number|string} compareAtPrice
   * @returns {number|null}
   */
  function discountPercent(price, compareAtPrice) {
    const currentPrice = Number(price);
    const crossedOutPrice = Number(compareAtPrice);
    if (!Number.isFinite(currentPrice) || !Number.isFinite(crossedOutPrice) || currentPrice < 0 || crossedOutPrice <= currentPrice) {
      return null;
    }
    return Math.round(((crossedOutPrice - currentPrice) / crossedOutPrice) * 100);
  }

  /**
   * Current wholesale per-bag display prices are client-approved fixed
   * merchandising values. Discount percentages are calculated separately.
   * @param {ProductPayload} product
   * @returns {{perBag: number}|null}
   */
  function wholesaleDisplayPricing(product) {
    if (!product?.isWholesale) return null;
    return product.id === WHOLESALE_DISPLAY_PRICING.pureCrunch.handle
      ? WHOLESALE_DISPLAY_PRICING.pureCrunch
      : WHOLESALE_DISPLAY_PRICING.default;
  }

  /**
   * Wholesale savings use one product-level case price so the promotional
   * percentage remains unchanged when customers switch pack variants. Retail
   * products retain their selected Shopify variant compare-at calculation.
   * @param {ProductPayload} product
   * @param {VariantPayload|null} [variant]
   * @returns {number|null}
   */
  function productDiscountPercent(product, variant = null) {
    const selectedVariant = (product.variants || []).find(variant => Number(variant.id) === Number(product.selectedVariantId))
      || (product.variants || []).find(variant => variant.available)
      || product.variants?.[0];
    if (product?.isWholesale) {
      const srpPerBag = Number(product.srp);
      if (!Number.isFinite(srpPerBag) || srpPerBag <= 0) return null;
      return discountPercent(
        product.price,
        srpPerBag * WHOLESALE_CASE_SIZE
      );
    }
    return discountPercent(
      variant?.price ?? selectedVariant?.price ?? product.price,
      variant?.compareAtPrice ?? selectedVariant?.compareAtPrice ?? product.compareAtPrice
    );
  }

  function imageIdentity(src) {
    if (typeof src !== 'string' || !src.trim()) return '';
    try {
      const url = new URL(src, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return src.split('?')[0];
    }
  }

  function boxLabel(count) {
    return count === 1 ? strings.boxSingular : strings.boxPlural;
  }

  function flavorLabel(count) {
    return count === 1 ? strings.flavorSingular : strings.flavorPlural;
  }

  function variantForItem(product, item) {
    return (product?.variants || []).find(variant => Number(variant.id) === Number(item?.variantId))
      || (product?.variants || []).find(variant => Number(variant.id) === Number(product?.selectedVariantId))
      || product?.variants?.[0]
      || null;
  }

  function nutritionValue(row) {
    if (!row.value && !row.dv) return row.name;
    return `${row.value}${row.dv ? ` ${row.dv}` : ''}`.trim();
  }

  function proteinScore(product) {
    const protein = (Array.isArray(product.nutrition) ? product.nutrition : [])
      .find(row => String(row?.name || '').toLowerCase() === 'protein');
    return protein ? parseInt(protein.value, 10) || 0 : 0;
  }

  function cartQuantity(cart) {
    return cart.reduce((sum, item) => sum + (item.qty || 0), 0);
  }

  function giftProgress(cart) {
    const count = cartQuantity(cart);
    return {
      count,
      needed: Math.max(0, (site.giftCardPromo?.threshold || 1) - count),
      unlocked: site.qualifiesForGiftCard ? site.qualifiesForGiftCard(cart) : count >= (site.giftCardPromo?.threshold || 1)
    };
  }

  async function addToCartWithPromo(id, qty = 1, variantId = null, properties = null, sellingPlan = null) {
    const product = site.getProductById(id);
    if (!product) {
      showErrorToast(strings.productNotFound);
      return;
    }
    try {
      await site.addToCart(id, qty, variantId, properties, sellingPlan);
    } catch (e) {
      showErrorToast(strings.cartError);
      return;
    }
    const progress = giftProgress(site.getCart());
    showToast(progress.unlocked
      ? interpolate(strings.promoUnlocked, { label: site.giftCardPromo.shortLabel })
      : interpolate(strings.productAdded, {
        product: product.name,
        count: progress.needed,
        box: boxLabel(progress.needed)
      }));
  }

  function showErrorToast(message) {
    showToast(message || strings.genericError);
  }

  function showToast(message) {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1800);
  }

  function generateOrderMailto({ product, variant = null, qty = 1, selectedVariantLabel = '', packLabel = '', purchaseOption = '', subscriptionCycle = '' }) {
    const catalogSettings = window.DELI_DELE_SITE?.catalogMode || {};
    const recipientEmail = catalogSettings.email || '';

    const productName = product?.name || product?.title || '';
    const subjectTemplate = catalogSettings.subjectTemplate || '';
    const subject = subjectTemplate.replace('[Product Name]', productName);

    const unitPrice = typeof variant?.price === 'number' ? variant.price : product?.price;
    const priceVal = typeof unitPrice === 'number' ? (unitPrice * qty) : null;
    const priceText = priceVal !== null ? site.money(priceVal) : '';
    const packInfo = packLabel || selectedVariantLabel;

    const orderLines = [
      interpolate(strings.orderDetailProduct, { product: productName }),
      interpolate(strings.orderDetailPack, { pack: packInfo }),
      interpolate(strings.orderDetailQuantity, { quantity: qty })
    ];
    if (priceText) {
      orderLines.push(interpolate(strings.orderDetailTotal, { total: priceText }));
    }
    if (purchaseOption) {
      const purchaseDetail = subscriptionCycle && purchaseOption.toLowerCase().includes('subscri')
        ? `${purchaseOption} (${subscriptionCycle})`
        : purchaseOption;
      orderLines.push(interpolate(strings.orderDetailPurchase, { purchase: purchaseDetail }));
    }

    const body = String(catalogSettings.bodyTemplate || '').replace('[Order Details]', orderLines.join('\n'));

    return `mailto:${recipientEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, char => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[char]);
  }

  function syncCartCount() {
    site.updateCartCount();
  }

  async function refreshCartFromServer() {
    if (site && typeof site.refreshFromShopifyCart === 'function') {
      await site.refreshFromShopifyCart();
    }
  }

  function closeQuickView(quickView, backdrop) {
    if (!quickView) return;
    quickView.hidden = true;
    backdrop.hidden = true;
    document.body.classList.remove('is-quick-view-open');
  }

  function initCollection() {
    const grid = document.querySelector('[data-product-grid]');
    if (!grid) return;

    const filters = Array.from(document.querySelectorAll('[data-filter]'));
    const sort = document.querySelector('[data-sort]');
    const quickView = document.querySelector('[data-quick-view]');
    const quickContent = document.querySelector('[data-quick-content]');
    const quickClose = document.querySelector('[data-quick-close]');
    const quickBackdrop = document.querySelector('[data-quick-backdrop]');
    const resultCount = document.querySelector('[data-result-count]');
    const emptyResults = document.querySelector('[data-empty-results]');
    const resetFilters = document.querySelector('[data-reset-filters]');
    let activeFilter = 'all';

    if (!PRODUCTS || PRODUCTS.length === 0) {
      grid.innerHTML = `
        <div class="collection-empty is-revealed" style="grid-column: 1 / -1; text-align: center; padding: 48px 0;">
          <h2>${escapeHtml(strings.noProducts)}</h2>
          <p>${escapeHtml(strings.noProductsHelp)}</p>
        </div>`;
      if (resultCount) resultCount.textContent = interpolate(strings.flavorCount, { count: 0, flavor: flavorLabel(0) });
      if (emptyResults) emptyResults.hidden = true;
      return;
    }

    function filteredProducts() {
      const sortValue = sort?.value || 'featured';
      const sorted = [...PRODUCTS].sort((a, b) => {
        if (sortValue === 'price-asc') return a.price - b.price;
        if (sortValue === 'protein-desc') return proteinScore(b) - proteinScore(a);
        return 0;
      });

      if (activeFilter === 'all') return sorted;
      const targetFilter = activeFilter.toLowerCase().replace(/[-_]/g, ' ').trim();

      return sorted.filter(product => {
        // 1. Tag matching
        const tags = Array.isArray(product.tags) ? product.tags : [];
        const hasTagMatch = tags.some(t => {
          const normTag = String(t).toLowerCase().replace(/[-_]/g, ' ').trim();
          return normTag === targetFilter || normTag.includes(targetFilter) || targetFilter.includes(normTag);
        });
        if (hasTagMatch) return true;

        // 2. Badge matching
        if (product.badge) {
          const normBadge = String(product.badge).toLowerCase().replace(/[-_]/g, ' ').trim();
          if (normBadge.includes(targetFilter)) return true;
        }

        // 3. Facts matching
        if (Array.isArray(product.facts)) {
          const hasFactMatch = product.facts.some(fact => {
            const normFact = String(fact).toLowerCase().replace(/[-_]/g, ' ').trim();
            return normFact.includes(targetFilter);
          });
          if (hasFactMatch) return true;
        }

        return false;
      });
    }

    function renderQuickView(product) {
      if (!product) return;
      const isCatalogMode = window.DELI_DELE_SITE?.catalogMode?.enabled !== false;
      const ctaText = isCatalogMode ? window.DELI_DELE_SITE?.catalogMode?.buttonText : strings.addToCart;
      const hasDiscount = Number(product.compareAtPrice) > Number(product.price);
      const priceHtml = `
        <div class="product-card__price">
          <span class="product-card__price-amount">${escapeHtml(site.money(product.price))}</span>
          ${hasDiscount ? `<s class="product-card__price-compare">${escapeHtml(site.money(product.compareAtPrice))}</s>` : ''}
        </div>`;

      quickContent.innerHTML = `
        <img src="${escapeHtml(product.image)}" alt="${escapeHtml(interpolate(strings.imageAlt, { product: product.name }))}" width="${Number(product.imageWidth) || 800}" height="${Number(product.imageHeight) || 800}" />
        <p class="eyebrow">${escapeHtml(product.badge)}</p>
        <h3>${escapeHtml(product.name)}</h3>
        ${priceHtml}
        <p>${escapeHtml(product.story || product.magic?.body || '')}</p>
        <ul>${(product.nutrition || []).slice(0, 5).map(row => `<li><span>${escapeHtml(row.name)}</span><strong>${escapeHtml(nutritionValue(row))}</strong></li>`).join('')}</ul>
        <div class="quick-view__actions">
          <a class="secondary-cta" href="${escapeHtml(product.url)}">${escapeHtml(strings.openProduct)}</a>
          <button class="primary-cta" type="button" data-quick-add="${escapeHtml(product.id)}">${escapeHtml(ctaText)}</button>
        </div>
      `;
      quickView.hidden = false;
      quickBackdrop.hidden = false;
      document.body.classList.add('is-quick-view-open');
      quickClose.focus();
    }

    function render() {
      const visibleProducts = filteredProducts();
      if (resultCount) {
        resultCount.textContent = interpolate(strings.flavorCount, {
          count: visibleProducts.length,
          flavor: flavorLabel(visibleProducts.length)
        });
      }
      if (emptyResults) {
        emptyResults.hidden = visibleProducts.length !== 0;
      }
      grid.innerHTML = visibleProducts.map((product, idx) => {
        const savePercent = productDiscountPercent(product);
        const saveBadge = savePercent == null
          ? ''
          : `<sup class="price-save-badge product-card__save">${escapeHtml(interpolate(strings.savePercent, { percent: savePercent }))}</sup>`;
        const hasDiscount = Number(product.compareAtPrice) > Number(product.price);
        const priceHtml = `
          <div class="product-card__price">
            <span class="product-card__price-amount">${escapeHtml(site.money(product.price))}</span>
            ${hasDiscount ? `<s class="product-card__price-compare">${escapeHtml(site.money(product.compareAtPrice))}</s>` : ''}
          </div>`;
        return `
          <article class="product-card reveal-on-scroll" data-card="${escapeHtml(product.id)}" style="transition-delay: ${idx * 80}ms;">
            <a class="product-card__media" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(interpolate(strings.viewProduct, { product: product.name }))}">
              <img src="${escapeHtml(product.image)}" alt="${escapeHtml(interpolate(strings.imageAlt, { product: product.name }))}" width="${Number(product.imageWidth) || 800}" height="${Number(product.imageHeight) || 800}" />
              ${saveBadge}
            </a>
            <div class="product-card__body">
              <div class="product-card__meta">
                <span>${escapeHtml(product.badge)}</span>
              </div>
              <h2>${escapeHtml(product.name)}</h2>
              ${priceHtml}
              <p>${escapeHtml(product.description)}</p>
              <div class="product-card__facts">${(product.facts || []).map(fact => `<span>${escapeHtml(fact)}</span>`).join('')}</div>
              <div class="product-card__actions">
                <button class="secondary-cta" type="button" data-quick="${escapeHtml(product.id)}">${escapeHtml(strings.quickView)}</button>
                <a class="primary-cta" href="${escapeHtml(product.url)}">${escapeHtml(strings.openProduct)}</a>
              </div>
            </div>
          </article>`;
      }).join('');
      document.dispatchEvent(new CustomEvent('content:updated'));
    }

    if (filters.length > 0) {
      filters.forEach(button => {
        button.addEventListener('click', () => {
          activeFilter = button.dataset.filter;
          filters.forEach(filter => {
            const active = filter === button;
            filter.classList.toggle('is-active', active);
            filter.setAttribute('aria-pressed', String(active));
          });
          render();
        });
        button.setAttribute('aria-pressed', String(button.classList.contains('is-active')));
      });
    }

    if (sort) {
      sort.addEventListener('change', render);
    }

    if (resetFilters) {
      resetFilters.addEventListener('click', () => {
        activeFilter = 'all';
        filters.forEach(filter => {
          const active = filter.dataset.filter === 'all';
          filter.classList.toggle('is-active', active);
          filter.setAttribute('aria-pressed', String(active));
        });
        if (sort) sort.value = 'featured';
        render();
      });
    }

    grid.addEventListener('click', async event => {
      const addButton = event.target.closest('[data-add]');
      const quickButton = event.target.closest('[data-quick]');
      if (addButton) {
        const targetProduct = site.getProductById(addButton.dataset.add);
        if (window.DELI_DELE_SITE?.catalogMode?.enabled !== false && targetProduct) {
          const mailtoUrl = generateOrderMailto({ product: targetProduct });
          showToast(strings.openingEmail);
          window.location.href = mailtoUrl;
          return;
        }
        await addToCartWithPromo(addButton.dataset.add);
      }
      if (quickButton) renderQuickView(site.getProductById(quickButton.dataset.quick));
    });

    quickContent.addEventListener('click', async event => {
      const add = event.target.closest('[data-quick-add]');
      if (!add) return;
      const targetProduct = site.getProductById(add.dataset.quickAdd);
      if (window.DELI_DELE_SITE?.catalogMode?.enabled !== false && targetProduct) {
        const mailtoUrl = generateOrderMailto({ product: targetProduct });
        showToast(strings.openingEmail);
        window.location.href = mailtoUrl;
        closeQuickView(quickView, quickBackdrop);
        return;
      }
      await addToCartWithPromo(add.dataset.quickAdd);
      closeQuickView(quickView, quickBackdrop);
    });

    quickClose.addEventListener('click', () => closeQuickView(quickView, quickBackdrop));
    quickBackdrop.addEventListener('click', () => closeQuickView(quickView, quickBackdrop));
    quickView.addEventListener('click', event => {
      if (event.target === quickView) closeQuickView(quickView, quickBackdrop);
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') closeQuickView(quickView, quickBackdrop);
    });

    render();
  }

  function initProduct() {
    const page = document.querySelector('[data-product-page]');
    if (!page) return;

    const params = new URLSearchParams(window.location.search);
    // Determine which product to show. Priority:
    // 1. URL ?flavor=... query param
    // 2. Liquid-rendered active flavor handle (set in sections/product.liquid)
    // 3. Path handle extracted from /products/{handle}
    // 4. First product as a safe fallback.
    const pathHandle = window.location.pathname.split('/products/')[1]?.split('/')[0]?.split('?')[0];
    const initialId = params.get('flavor') || window.DELI_DELE_SITE?.activeFlavorOverride || pathHandle || PRODUCTS[0]?.id;
    let product = site.getProductById(initialId);

    if (!product) {
      page.innerHTML = `
        <section class="product-detail" style="text-align:center; padding: 80px 20px;">
          <h1>${escapeHtml(strings.productNotFound)}</h1>
          <p>${escapeHtml(strings.productLoadError)}</p>
          <a class="primary-cta" href="${escapeHtml(site.routes.retailCatalog)}">${escapeHtml(strings.browseFlavors)}</a>
        </section>`;
      return;
    }
    let qty = 1;
    let tab = 'inside';
    const requestedVariantId = Number(params.get('variant') || site.productPage?.selectedVariantId || product.selectedVariantId);
    let packIndex = Math.max(0, (product.variants || []).findIndex(variant => Number(variant.id) === requestedVariantId));
    let purchaseOptionId = product.requiresSellingPlan ? 'subscription' : (site.purchaseOptions[0]?.id || 'one-time');
    let deliveryFrequency = site.subscriptionDetail?.frequencies?.[0] || '';
    let selectedSellingPlanId = null;

    const image = document.querySelector('[data-product-image]');
    const title = document.querySelector('[data-product-title]');
    const description = document.querySelector('[data-product-description]');
    const price = document.querySelector('[data-product-price]');
    const priceNote = document.querySelector('[data-product-price-note]');
    const srpNote = document.querySelector('[data-product-srp-note]');
    const thumbs = document.querySelector('[data-product-thumbs]');
    const packTypeCurrent = document.querySelector('[data-pack-type-current]');
    const packTypeSelect = document.querySelector('[data-pack-type-select]');
    const purchaseOptionsEl = document.querySelector('[data-purchase-options]');
    const subscriptionDetail = document.querySelector('[data-subscription-detail]');
    const subscriptionSummary = document.querySelector('[data-subscription-summary]');
    const subscriptionDetailCopy = document.querySelector('[data-subscription-detail-copy]');
    const deliveryFrequencyGroup = document.querySelector('[data-delivery-frequency-group]');
    const deliveryFrequencySelect = document.querySelector('[data-delivery-frequency-select]');
    const customSubscriptionGroup = document.querySelector('[data-custom-subscription-group]');
    const qtyOutput = document.querySelector('[data-qty]');
    const tabPanel = document.querySelector('[data-product-tab-panel]');
    const related = document.querySelector('[data-related-products]');
    const stickyName = document.querySelector('[data-sticky-name]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const productCopy = item => item.pdpDescription || item.description;
    const packOptionsFor = item => (item.variants || []).map(variant => {
      const metadataOptions = Array.isArray(item.packOptions) ? item.packOptions : [];
      const metadata = metadataOptions.find(option => option
        && String(option.label || '').trim().toLowerCase() === String(variant.title).trim().toLowerCase()) || {};
      return {
        ...metadata,
        label: variant.title,
        content: metadata.content || 'single',
        shippingNote: metadata.shippingNote || item.shippingNote || '',
        variant
      };
    });
    const purchaseOptionById = id => site.purchaseOptions.find(option => option.id === id) || site.purchaseOptions[0];

    function selectedPackOption() {
      return packOptionsFor(product)[packIndex] || packOptionsFor(product)[0];
    }

    function selectedVariant() {
      return selectedPackOption()?.variant || product.variants?.[0] || null;
    }

    function getActiveSellingPlanGroup() {
      if (!product || !Array.isArray(product.sellingPlanGroups) || !product.sellingPlanGroups.length) return null;
      const isWs = Boolean(product.isWholesale);
      if (isWs) {
        return product.sellingPlanGroups.find(g => g.name && g.name.toLowerCase().includes('wholesale')) || null;
      }
      return product.sellingPlanGroups.find(g => g.name && !g.name.toLowerCase().includes('wholesale')) || null;
    }

    function getVariantSellingPlans() {
      const activeGroup = getActiveSellingPlanGroup();
      if (!activeGroup) return [];
      const variant = selectedVariant();
      const variantAllocations = variant?.sellingPlanAllocations;

      if (Array.isArray(variantAllocations) && variantAllocations.length > 0) {
        const matchingAllocations = variantAllocations.filter(alloc =>
          !alloc.sellingPlanGroupId || String(alloc.sellingPlanGroupId) === String(activeGroup.id)
        );
        if (matchingAllocations.length > 0) {
          return matchingAllocations.map(alloc => {
            const planDetail = (activeGroup.sellingPlans || []).find(p => String(p.id) === String(alloc.sellingPlanId)) || {};
            return {
              id: alloc.sellingPlanId,
              name: alloc.sellingPlanName || planDetail.name || 'Subscription',
              price: alloc.price,
              compareAtPrice: alloc.compareAtPrice,
              perDeliveryPrice: alloc.perDeliveryPrice,
              unitPrice: alloc.unitPrice,
              description: planDetail.description || activeGroup.summary || '',
              priceAdjustments: planDetail.priceAdjustments || []
            };
          });
        }
        return [];
      }

      return (activeGroup.sellingPlans || []).map(plan => ({
        id: plan.id,
        name: plan.name || 'Subscription',
        price: null,
        compareAtPrice: null,
        perDeliveryPrice: null,
        unitPrice: null,
        description: plan.description || activeGroup.summary || '',
        priceAdjustments: plan.priceAdjustments || []
      }));
    }

    function currentSellingPlan() {
      const plans = getVariantSellingPlans();
      if (!plans.length) return null;
      return plans.find(p => String(p.id) === String(selectedSellingPlanId)) || plans[0] || null;
    }

    function getSellingPlanDiscount(plan, variant) {
      if (!plan) return 0;

      // 1. Direct percentage from price adjustments on the plan
      if (Array.isArray(plan.priceAdjustments) && plan.priceAdjustments.length > 0) {
        const adj = plan.priceAdjustments[0];
        const valType = (adj.valueType || adj.adjustmentType || '').toLowerCase();
        const val = Number(adj.value ?? adj.adjustmentValue ?? 0);
        if (valType === 'percentage' && val > 0) {
          return Math.round(val);
        }
      }

      // 2. Calculated from allocation price vs compareAtPrice or variant price
      const basePrice = Number(variant?.price ?? product.price ?? 0);
      const allocPrice = Number(plan.price ?? 0);
      const comparePrice = Number(plan.compareAtPrice ?? basePrice);
      if (allocPrice > 0 && comparePrice > allocPrice) {
        const pct = Math.round(((comparePrice - allocPrice) / comparePrice) * 100);
        if (pct > 0) return pct;
      }
      if (allocPrice > 0 && basePrice > allocPrice) {
        const pct = Math.round(((basePrice - allocPrice) / basePrice) * 100);
        if (pct > 0) return pct;
      }

      // 3. Fixed amount adjustment converted to percentage
      if (Array.isArray(plan.priceAdjustments) && plan.priceAdjustments.length > 0) {
        const adj = plan.priceAdjustments[0];
        const valType = (adj.valueType || adj.adjustmentType || '').toLowerCase();
        if (valType === 'fixed_amount' && basePrice > 0) {
          const discountVal = Number(adj.value || 0) / 100;
          const pct = Math.round((discountVal / basePrice) * 100);
          if (pct > 0) return pct;
        }
      }

      return 0;
    }

    function getSubscriptionPrice(plan) {
      const variant = selectedVariant();
      const basePrice = variant?.price ?? product.price;

      if (plan) {
        if (typeof plan.price === 'number' && plan.price > 0) {
          return plan.price;
        }
        const pct = getSellingPlanDiscount(plan, variant);
        if (pct > 0) {
          return basePrice * (1 - (pct / 100));
        }
      }
      return basePrice;
    }

    function getSubscriptionBadge(plan) {
      if (!plan) return '';
      const variant = selectedVariant();
      const pct = getSellingPlanDiscount(plan, variant);
      if (pct > 0) {
        return `SAVE ${pct}%`;
      }
      return '';
    }

    function selectedInsideContent() {
      const packOption = selectedPackOption();
      const isAssortment = packOption?.content === 'assortment';
      return {
        body: packOption?.insideCopy || (isAssortment
          ? site.productPage?.assortmentPackInsideCopy
          : site.productPage?.singlePackInsideCopy)
      };
    }

    function purchaseOptionPrice(option) {
      if (option.id === 'subscription') {
        return site.money(getSubscriptionPrice(currentSellingPlan()));
      }
      const variantPrice = selectedVariant()?.price ?? product.price;
      return site.money(option.discount ? variantPrice * (1 - option.discount) : variantPrice);
    }

    function selectedUnitCount() {
      const configuredCount = Number(selectedPackOption()?.unitCount);
      if (Number.isFinite(configuredCount) && configuredCount > 0) return configuredCount;
      const match = selectedPackOption()?.label?.match(/(\d+)\s*ct/i);
      return match ? Number(match[1]) : Number(site.productPage?.defaultUnitCount || 1);
    }

    function priceNoteText() {
      const configuredNoteTemplate = product.isWholesale
        ? priceNote?.dataset.wholesalePriceNoteTemplate
        : priceNote?.dataset.retailPriceNoteTemplate;
      const noteTemplate = product.isWholesale
        ? configuredNoteTemplate || strings.wholesalePricePerBag
        : configuredNoteTemplate || strings.retailPricePerBag;
      const wholesalePricing = wholesaleDisplayPricing(product);
      const unitCount = selectedUnitCount();
      const perBagPrice = wholesalePricing?.perBag
        ?? (selectedVariant()?.price ?? product.price) / unitCount;
      return interpolate(noteTemplate, {
        price: site.money(perBagPrice)
      });
    }

    function srpNoteText() {
      const srpPerBag = Number(product?.srp);
      if (!Number.isFinite(srpPerBag) || srpPerBag <= 0) return '';
      if (product?.isWholesale) {
        const wholesaleTemplate = srpNote?.dataset?.wholesaleSuggestedPriceTemplate
          || strings.suggestedRetailPricePerBag
          || 'Suggested Retail Price: [price]/bag';
        return interpolate(wholesaleTemplate, {
          price: site.money(srpPerBag)
        });
      }

      const retailTemplate = srpNote?.dataset?.retailSuggestedPriceTemplate;
      if (retailTemplate !== undefined && retailTemplate.trim() === '') return '';
      let template = (retailTemplate && retailTemplate.trim()) || 'Suggested retail price up to [price] per bag';
      if (template.includes('[price]')) {
        return interpolate(template, {
          price: site.money(srpPerBag)
        });
      }
      if (/\$\d+(\.\d+)?/.test(template)) {
        return interpolate(template.replace(/\$\d+(\.\d+)?/, '[price]'), {
          price: site.money(srpPerBag)
        });
      }
      return interpolate(`${template.trim()} [price] per bag`, {
        price: site.money(srpPerBag)
      });
    }

    function renderSrpNote() {
      if (!srpNote) return;
      const note = srpNoteText();
      srpNote.hidden = note === '';
      srpNote.textContent = note;
    }

    function renderRelated() {
      if (!related) return;
      const relatedLimit = Number(site.productPage?.relatedLimit || 4);
      const relatedProducts = PRODUCTS
        .filter(item => item.id !== product.id && Boolean(item.isWholesale) === Boolean(product.isWholesale))
        .slice(0, relatedLimit);
      related.innerHTML = relatedProducts.map(item => `
        <article class="related-card">
          <a class="related-card__media" href="${escapeHtml(item.url)}" aria-label="${escapeHtml(interpolate(strings.viewProduct, { product: item.name }))}">
            <img src="${escapeHtml(item.image)}" alt="${escapeHtml(interpolate(strings.imageAlt, { product: item.name }))}" width="${Number(item.imageWidth) || 800}" height="${Number(item.imageHeight) || 800}" loading="lazy" />
          </a>
          <div>
            <p class="product-card__meta">${escapeHtml(item.badge)}</p>
            <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.name)}</a></h3>
            <p><a href="${escapeHtml(item.url)}" tabindex="-1">${escapeHtml(item.description)}</a></p>
          </div>
          <div class="related-card__actions">
            <a class="secondary-cta" href="${escapeHtml(item.url)}">${escapeHtml(strings.view)}</a>
            <button class="primary-cta" type="button" data-related-add="${escapeHtml(item.id)}">${escapeHtml(strings.add)}</button>
          </div>
        </article>
      `).join('');
    }

    function renderPackOptions() {
      const packOptions = packOptionsFor(product);
      packIndex = Math.min(packIndex, packOptions.length - 1);

      if (packTypeCurrent) {
        packTypeCurrent.textContent = selectedPackOption().label;
      }

      if (priceNote) {
        priceNote.textContent = priceNoteText();
      }
      renderSrpNote();

      if (packTypeSelect) {
        packTypeSelect.innerHTML = packOptions.map((option, index) => `
          <option value="${index}" ${index === packIndex ? 'selected' : ''} ${site.catalogMode?.enabled === false && !option.variant.available ? 'disabled' : ''}>${escapeHtml(option.label)}</option>
        `).join('');
        packTypeSelect.disabled = packOptions.length < 2;
      }
    }

    function renderPurchaseOptions() {
      if (!purchaseOptionsEl) return;

      const purchaseOptionsBlock = purchaseOptionsEl.closest('[data-purchase-options-block]') || purchaseOptionsEl.closest('.option-block');
      const activeGroup = getActiveSellingPlanGroup();
      const plans = getVariantSellingPlans();
      if (plans.length > 0) {
        if (!selectedSellingPlanId || !plans.some(p => String(p.id) === String(selectedSellingPlanId))) {
          selectedSellingPlanId = plans[0].id;
        }
      } else {
        selectedSellingPlanId = null;
      }
      const currentPlan = currentSellingPlan();
      const requiresPlan = Boolean(product.requiresSellingPlan);

      let effectivePurchaseOptions = [];
      if (!requiresPlan) {
        const oneTimeOption = site.purchaseOptions?.find(o => o.id === 'one-time') || { id: 'one-time', label: 'One Time Purchase', discount: 0 };
        effectivePurchaseOptions.push({
          id: 'one-time',
          label: oneTimeOption.label,
          badge: null,
          price: selectedVariant()?.price ?? product.price,
          comparePrice: null
        });
      }

      if (plans.length > 0) {
        const subConfig = site.purchaseOptions?.find(o => o.id === 'subscription') || {};
        const subLabel = subConfig.label || 'Subscribe and Save';
        const subPrice = getSubscriptionPrice(currentPlan);
        const basePrice = selectedVariant()?.price ?? product.price;
        const badge = getSubscriptionBadge(currentPlan);
        effectivePurchaseOptions.push({
          id: 'subscription',
          label: subLabel,
          badge,
          price: subPrice,
          comparePrice: subPrice < basePrice ? basePrice : null
        });
      }

      if (requiresPlan && purchaseOptionId !== 'subscription') {
        purchaseOptionId = 'subscription';
      } else if (!effectivePurchaseOptions.some(opt => opt.id === purchaseOptionId)) {
        purchaseOptionId = effectivePurchaseOptions[0]?.id || 'one-time';
      }

      const subscriptionCycleSelect = document.querySelector('[name="properties[subscribe]"]') || document.querySelector('[data-subscription-cycle-select]');
      const hiddenSellingPlanInput = document.querySelector('[data-selling-plan-input]');

      const hasSubscriptionOptions = plans.length > 0;
      if (!hasSubscriptionOptions && !requiresPlan) {
        if (purchaseOptionsBlock) {
          purchaseOptionsBlock.hidden = true;
          purchaseOptionsBlock.style.display = 'none';
        }
        if (purchaseOptionsEl) {
          purchaseOptionsEl.innerHTML = '';
        }
        if (hiddenSellingPlanInput) {
          hiddenSellingPlanInput.value = '';
        }
        if (deliveryFrequencyGroup) {
          deliveryFrequencyGroup.hidden = true;
          deliveryFrequencyGroup.style.display = 'none';
        }
        if (customSubscriptionGroup) {
          customSubscriptionGroup.hidden = true;
          customSubscriptionGroup.style.display = 'none';
        }
        if (subscriptionDetail) {
          subscriptionDetail.hidden = true;
        }
        return;
      }

      if (purchaseOptionsBlock) {
        purchaseOptionsBlock.hidden = false;
        purchaseOptionsBlock.style.display = '';
      }

      purchaseOptionsEl.innerHTML = effectivePurchaseOptions.map(option => `
        <label class="purchase-option ${option.id === purchaseOptionId ? 'is-active' : ''}">
          <input type="radio" name="purchase-option" value="${option.id}" ${option.id === purchaseOptionId ? 'checked' : ''} />
          <span class="purchase-option__radio" aria-hidden="true"></span>
          <span class="purchase-option__copy">
            <span class="purchase-option__title">${escapeHtml(option.label)}</span>
            ${option.badge ? `<span class="purchase-option__badge">${escapeHtml(option.badge)}</span>` : ''}
          </span>
          <span class="purchase-option__price">
            <strong>${site.money(option.price)}</strong>
            ${option.comparePrice ? `<span>${site.money(option.comparePrice)}</span>` : ''}
          </span>
        </label>
      `).join('');

      if (subscriptionDetail) {
        subscriptionDetail.hidden = !activeGroup;
      }

      if (subscriptionSummary) {
        subscriptionSummary.textContent = activeGroup?.summary || site.subscriptionDetail?.summary || 'Subscription Detail';
      }

      if (subscriptionDetailCopy) {
        subscriptionDetailCopy.textContent = currentPlan?.description || activeGroup?.summary || site.subscriptionDetail?.body || '';
      }

      if (plans.length > 0) {
        const planOptionsHtml = plans.map(p => `
          <option value="${escapeHtml(p.id)}" data-plan-name="${escapeHtml(p.name)}" ${String(p.id) === String(selectedSellingPlanId) ? 'selected' : ''}>
            ${escapeHtml(p.name)}
          </option>
        `).join('');

        if (subscriptionCycleSelect) {
          subscriptionCycleSelect.innerHTML = planOptionsHtml;
        }
        if (deliveryFrequencySelect) {
          deliveryFrequencySelect.innerHTML = planOptionsHtml;
        }
        if (hiddenSellingPlanInput) {
          hiddenSellingPlanInput.value = purchaseOptionId === 'subscription' && currentPlan ? currentPlan.id : '';
        }
      } else {
        if (subscriptionCycleSelect) {
          subscriptionCycleSelect.innerHTML = '';
        }
        if (deliveryFrequencySelect) {
          deliveryFrequencySelect.innerHTML = '';
        }
        if (hiddenSellingPlanInput) {
          hiddenSellingPlanInput.value = '';
        }
      }

      const isSub = purchaseOptionId === 'subscription';
      if (deliveryFrequencyGroup) {
        deliveryFrequencyGroup.hidden = !isSub;
        deliveryFrequencyGroup.style.display = isSub ? 'grid' : 'none';
      }

      if (customSubscriptionGroup) {
        customSubscriptionGroup.hidden = !isSub;
        customSubscriptionGroup.style.display = isSub ? 'grid' : 'none';
      }
    }

    function renderTabs() {
      const packOption = selectedPackOption();
      const insideContent = selectedInsideContent();

      document.querySelectorAll('[data-product-tab]').forEach(button => {
        const active = button.dataset.productTab === tab;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });

      if (tab === 'inside') {
        tabPanel.innerHTML = `<p>${escapeHtml(insideContent.body)}</p>`;
      } else {
        tabPanel.innerHTML = `<p>${escapeHtml(product.shipping)}</p>`;
      }
    }

    function renderPickers() {
      const variantImages = (product.variants || []).map(variant => variant.featuredMedia?.src).filter(Boolean);
      const imageCandidates = [...(product.images || []), ...variantImages, product.image].filter(Boolean);
      const imageKeys = new Set();
      const imgList = imageCandidates.filter(imgUrl => {
        const key = imageIdentity(imgUrl);
        if (!key || imageKeys.has(key)) return false;
        imageKeys.add(key);
        return true;
      });
      const activeImageKey = imageIdentity(image.src);
      thumbs.innerHTML = imgList.map((imgUrl, index) => `
        <button type="button" class="thumb-btn ${imageIdentity(imgUrl) === activeImageKey ? 'is-active' : ''}" data-product-image-thumb="${escapeHtml(imgUrl)}" aria-label="${escapeHtml(interpolate(strings.viewProductImage, { index: index + 1 }))}" aria-pressed="${imageIdentity(imgUrl) === activeImageKey}">
          <img src="${escapeHtml(imgUrl)}" alt="" width="96" height="96" />
        </button>
      `).join('');
    }

    function renderSelectedVariant({ updateUrl = true, animate = false } = {}) {
      const variant = selectedVariant();
      if (!variant) return;
      const media = variant.featuredMedia;
      const nextImage = media?.src || product.image;
      const updateImage = () => {
        image.src = nextImage;
        image.alt = media?.alt || interpolate(strings.imageAlt, { product: product.name });
        if (media?.width) image.width = media.width;
        if (media?.height) image.height = media.height;
        image.style.opacity = '';
        image.style.transform = '';
        renderPickers();
      };
      if (animate) {
        image.style.opacity = '0';
        image.style.transform = 'translateY(8px)';
        window.setTimeout(updateImage, 180);
      } else {
        updateImage();
      }

      const currentPrice = purchaseOptionId === 'subscription'
        ? getSubscriptionPrice(currentSellingPlan())
        : variant.price;

      if (price) {
        let priceHTML = escapeHtml(site.money(currentPrice));
        const savePercent = purchaseOptionId === 'subscription'
          ? (variant.price > currentPrice ? Math.round(((variant.price - currentPrice) / variant.price) * 100) : null)
          : productDiscountPercent(product, variant);
        if (savePercent != null && savePercent > 0) {
          priceHTML += ` <sup class="price-save-badge">${escapeHtml(interpolate(strings.savePercent, { percent: savePercent }))}</sup>`;
        }
        price.innerHTML = priceHTML;
      }
      if (stickyPrice) stickyPrice.textContent = site.money(currentPrice);
      if (priceNote) priceNote.textContent = priceNoteText();
      renderSrpNote();
      const canOrder = site.catalogMode?.enabled !== false || variant.available;
      document.querySelectorAll('[data-add-current], [data-add-current-sticky]').forEach(button => {
        button.disabled = !canOrder;
        button.textContent = canOrder
          ? (site.catalogMode?.enabled !== false ? site.catalogMode.buttonText : strings.addToCart)
          : strings.soldOut;
      });

      if (updateUrl) {
        const nextUrl = new URL(product.url, window.location.origin);
        nextUrl.searchParams.set('variant', variant.id);
        history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}`);
      }
    }

    function setProduct(id) {
      const resolved = site.getProductById(id);
      if (!resolved || !resolved.id) return;
      product = resolved;
      const initialVariantId = Number(params.get('variant') || site.productPage?.selectedVariantId || resolved.selectedVariantId);
      packIndex = Math.max(0, (resolved.variants || []).findIndex(variant => Number(variant.id) === initialVariantId));
      selectedSellingPlanId = null;
      purchaseOptionId = resolved.requiresSellingPlan ? 'subscription' : (site.purchaseOptions[0]?.id || 'one-time');
      deliveryFrequency = site.subscriptionDetail?.frequencies?.[0] || '';

      // Map color glows to each flavor variant
      const orbGlows = [
        'radial-gradient(circle, rgba(124, 77, 255, 0.45) 0%, transparent 70%)',
        'radial-gradient(circle, rgba(76, 175, 80, 0.45) 0%, transparent 70%)',
        'radial-gradient(circle, rgba(255, 171, 0, 0.45) 0%, transparent 70%)',
        'radial-gradient(circle, rgba(255, 61, 0, 0.45) 0%, transparent 70%)',
        'radial-gradient(circle, rgba(177, 64, 166, 0.45) 0%, transparent 70%)'
      ];
      const productIndex = Math.max(0, PRODUCTS.findIndex(candidate => candidate.id === product.id));
      const activeColor = orbGlows[productIndex % orbGlows.length];
      document.querySelector('.product-gallery__orb')?.style.setProperty('--active-flavor-orb', activeColor);
      window.setTimeout(() => {
        title.textContent = product.name;
        description.textContent = productCopy(product);
        stickyName.textContent = product.name;
        renderPackOptions();
        renderSelectedVariant({ updateUrl: false, animate: true });
        renderPurchaseOptions();
        renderTabs();
        renderRelated();
      }, 180);
    }

    function updateQty(nextQty) {
      qty = Math.max(1, nextQty);
      qtyOutput.textContent = qty;
      document.querySelectorAll('[data-current-qty]').forEach(el => { el.textContent = qty; });
    }

    page.addEventListener('click', event => {
      const thumb = event.target.closest('[data-product-image-thumb]');
      if (thumb) {
        const newSrc = thumb.dataset.productImageThumb;
        image.src = newSrc;

        // Update active class on thumbnails
        page.querySelectorAll('[data-product-image-thumb]').forEach(btn => {
          const active = btn.dataset.productImageThumb === newSrc;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', String(active));
        });
      }
    });

    if (packTypeSelect) {
      packTypeSelect.addEventListener('change', event => {
        packIndex = Number(event.target.value) || 0;
        renderPackOptions();
        renderSelectedVariant({ updateUrl: true, animate: true });
        renderPurchaseOptions();
        renderTabs();
      });
    }

    if (purchaseOptionsEl) {
      purchaseOptionsEl.addEventListener('change', event => {
        if (event.target.name !== 'purchase-option') return;
        purchaseOptionId = event.target.value;
        const currentPlan = currentSellingPlan();
        const hiddenInput = document.querySelector('[data-selling-plan-input]');
        if (hiddenInput) {
          hiddenInput.value = purchaseOptionId === 'subscription' && currentPlan ? currentPlan.id : '';
        }
        renderPurchaseOptions();
        renderSelectedVariant({ updateUrl: false });
      });
    }

    function handlePlanSelection(newPlanId) {
      selectedSellingPlanId = newPlanId;
      const plans = getVariantSellingPlans();
      const matched = plans.find(p => String(p.id) === String(newPlanId));
      if (matched) {
        deliveryFrequency = matched.name;
      } else {
        deliveryFrequency = newPlanId;
      }
      const subSelect = document.querySelector('[name="properties[subscribe]"]') || document.querySelector('[data-subscription-cycle-select]');
      if (subSelect && subSelect.value !== String(newPlanId)) {
        subSelect.value = newPlanId;
      }
      if (deliveryFrequencySelect && deliveryFrequencySelect.value !== String(newPlanId)) {
        deliveryFrequencySelect.value = newPlanId;
      }
      const hiddenInput = document.querySelector('[data-selling-plan-input]');
      if (hiddenInput) {
        hiddenInput.value = purchaseOptionId === 'subscription' && matched ? matched.id : '';
      }
      renderPurchaseOptions();
      renderSelectedVariant({ updateUrl: false });
    }

    const subCycleSelect = document.querySelector('[name="properties[subscribe]"]') || document.querySelector('[data-subscription-cycle-select]');
    if (subCycleSelect) {
      subCycleSelect.addEventListener('change', event => {
        handlePlanSelection(event.target.value);
      });
    }

    if (deliveryFrequencySelect) {
      deliveryFrequencySelect.addEventListener('change', event => {
        handlePlanSelection(event.target.value);
      });
    }

    document.querySelector('[data-qty-minus]').addEventListener('click', () => updateQty(qty - 1));
    document.querySelector('[data-qty-plus]').addEventListener('click', () => updateQty(qty + 1));
    document.querySelector('.qty-control').addEventListener('keydown', event => {
      if (event.key === 'ArrowUp') { event.preventDefault(); updateQty(qty + 1); }
      if (event.key === 'ArrowDown') { event.preventDefault(); updateQty(qty - 1); }
    });

    const addCurrent = async () => {
      const selectedOption = selectedPackOption();
      const isCatalogMode = window.DELI_DELE_SITE?.catalogMode?.enabled !== false;
      const plans = getVariantSellingPlans();
      const currentPlan = currentSellingPlan();

      if (isCatalogMode) {
        const subSelect = document.querySelector('[name="properties[subscribe]"]') || document.querySelector('[data-subscription-cycle-select]');
        const chosenCycleName = currentPlan ? currentPlan.name : (subSelect?.options?.[subSelect.selectedIndex]?.dataset?.planName || subSelect?.value || deliveryFrequency || '');
        const mailtoUrl = generateOrderMailto({
          product,
          variant: selectedVariant(),
          qty,
          selectedVariantLabel: selectedOption?.label || '',
          packLabel: selectedOption?.label || '',
          purchaseOption: purchaseOptionById(purchaseOptionId)?.label || (purchaseOptionId === 'subscription' ? 'Subscription' : 'One Time Purchase'),
          subscriptionCycle: purchaseOptionId === 'subscription' ? chosenCycleName : ''
        });
        showToast(strings.openingEmail);
        window.location.href = mailtoUrl;
        return;
      }

      const variantId = selectedVariant()?.id;

      let properties = null;
      let sellingPlanId = null;

      if (purchaseOptionId === 'subscription') {
        if (currentPlan) {
          sellingPlanId = currentPlan.id;
          properties = {
            "subscribe": currentPlan.name
          };
        } else {
          const subSelect = document.querySelector('[name="properties[subscribe]"]') || document.querySelector('[data-subscription-cycle-select]');
          if (subSelect) {
            const selectedOpt = subSelect.options?.[subSelect.selectedIndex];
            const planIdVal = Number(subSelect.value);
            if (!Number.isNaN(planIdVal) && planIdVal > 0) {
              sellingPlanId = planIdVal;
            }
            properties = {
              "subscribe": selectedOpt?.dataset?.planName || selectedOpt?.textContent?.trim() || subSelect.value
            };
          }
        }
      }

      await addToCartWithPromo(product.id, qty, variantId, properties, sellingPlanId);
    };

    const mainAddBtn = document.querySelector('[data-add-current]');
    const stickyAddBtn = document.querySelector('[data-add-current-sticky]');
    const isCatalogMode = window.DELI_DELE_SITE?.catalogMode?.enabled !== false;
    const catalogButtonText = isCatalogMode ? window.DELI_DELE_SITE?.catalogMode?.buttonText : strings.addToCart;
    if (mainAddBtn) mainAddBtn.textContent = catalogButtonText;
    if (stickyAddBtn) stickyAddBtn.textContent = catalogButtonText;

    if (mainAddBtn) mainAddBtn.addEventListener('click', addCurrent);
    if (stickyAddBtn) stickyAddBtn.addEventListener('click', addCurrent);
    document.querySelectorAll('[data-product-tab]').forEach(button => {
      button.setAttribute('role', 'tab');
      button.addEventListener('click', () => {
        tab = button.dataset.productTab;
        renderTabs();
      });
    });

    related?.addEventListener('click', async event => {
      const add = event.target.closest('[data-related-add]');
      if (!add) return;
      const relProd = site.getProductById(add.dataset.relatedAdd);
      if (window.DELI_DELE_SITE?.catalogMode?.enabled !== false && relProd) {
        const mailtoUrl = generateOrderMailto({ product: relProd });
        showToast(strings.openingEmail);
        window.location.href = mailtoUrl;
        return;
      }
      await addToCartWithPromo(add.dataset.relatedAdd);
    });

    renderPickers();
    setProduct(product.id);
    updateQty(qty);

    // Dynamic Zoom Lightbox initialization
    let lightbox = document.querySelector('[data-lightbox]');
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.className = 'lightbox';
      lightbox.dataset.lightbox = 'true';
      lightbox.hidden = true;
      lightbox.innerHTML = `
        <button type="button" class="lightbox__close" data-lightbox-close aria-label="${escapeHtml(strings.closeLightbox)}">&times;</button>
        <div class="lightbox__content">
          <img class="lightbox__image" data-lightbox-image src="" alt="" />
        </div>
      `;
      document.body.appendChild(lightbox);
    }

    if (image) {
      image.style.cursor = 'zoom-in';
      image.addEventListener('click', () => {
        const lightboxImg = lightbox.querySelector('[data-lightbox-image]');
        const lightboxClose = lightbox.querySelector('[data-lightbox-close]');
        if (lightboxImg) {
          lightboxImg.src = image.src;
          lightboxImg.alt = image.alt;
          lightboxImg.classList.remove('is-zoomed');
          lightboxImg.style.transformOrigin = 'center center';
        }
        lightbox.hidden = false;
        document.body.classList.add('is-lightbox-open');
        if (lightboxClose) lightboxClose.focus();
      });
    }

    lightbox.addEventListener('click', event => {
      const closeBtn = event.target.closest('[data-lightbox-close]');
      const lightboxImg = event.target.closest('[data-lightbox-image]');

      if (closeBtn || !lightboxImg) {
        lightbox.hidden = true;
        document.body.classList.remove('is-lightbox-open');
      } else if (lightboxImg) {
        lightboxImg.classList.toggle('is-zoomed');
        if (!lightboxImg.classList.contains('is-zoomed')) {
          lightboxImg.style.transformOrigin = 'center center';
        }
      }
    });

    const handleLightboxMouseMove = event => {
      const lightboxImg = lightbox.querySelector('[data-lightbox-image]');
      if (!lightboxImg || !lightboxImg.classList.contains('is-zoomed')) return;
      const rect = lightboxImg.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      lightboxImg.style.transformOrigin = `${x}% ${y}%`;
    };

    lightbox.addEventListener('mousemove', handleLightboxMouseMove);

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !lightbox.hidden) {
        lightbox.hidden = true;
        document.body.classList.remove('is-lightbox-open');
      }
    });

    // Sticky bottom CTA scroll and resize observers
    const stickyCta = document.querySelector('[data-sticky-cta]');

    if (stickyCta && mainAddBtn) {
      const handleScroll = () => {
        if (window.innerWidth <= 768) {
          const rect = mainAddBtn.getBoundingClientRect();
          // Toggle display if the main add-to-cart button has been scrolled past
          if (rect.bottom < 0) {
            stickyCta.style.display = 'flex';
            document.body.classList.add('has-mobile-sticky-cta');
          } else {
            stickyCta.style.display = 'none';
            document.body.classList.remove('has-mobile-sticky-cta');
          }
        } else {
          stickyCta.style.display = 'none';
          document.body.classList.remove('has-mobile-sticky-cta');
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll);
      // Run once on load to establish correct state
      handleScroll();
    }
  }

  async function initCart() {
    const items = document.querySelector('[data-cart-items]');
    if (!items) return;

    // Re-hydrate cart state from Shopify when landing on the cart page so the
    // displayed cart reflects the server-side cart (this fixes items appearing
    // briefly then disappearing because of stale localStorage state).
    await refreshCartFromServer();

    const subtotalEl = document.querySelector('[data-subtotal]');
    const discountEl = document.querySelector('[data-discount]');
    const totalEl = document.querySelector('[data-total]');
    const cartPromo = document.querySelector('[data-cart-promo]');
    const confirmation = document.querySelector('[data-checkout-confirmation]');
    const confirmationCopy = document.querySelector('[data-confirmation-copy]');
    const confirmationDetails = document.querySelector('[data-confirmation-details]');
    const confirmationClose = document.querySelector('[data-confirmation-close]');
    const emailPreview = document.querySelector('[data-email-preview]');
    const cartMessage = document.querySelector('[data-cart-message]');
    const cartMessageText = document.querySelector('[data-cart-message-text]');
    const cartUndo = document.querySelector('[data-cart-undo]');
    let discountRate = 0;
    let lastRemoved = null;

    function showUndoMessage(message, removedItem) {
      lastRemoved = removedItem;
      if (cartMessageText) cartMessageText.textContent = message;
      if (cartMessage) cartMessage.hidden = false;
    }

    function render() {
      const cart = site.getCart();
      const progress = giftProgress(cart);
      const cartPage = document.querySelector('[data-cart-page]');
      if (cartPage) {
        cartPage.classList.toggle('is-empty', cart.length === 0);
      }
      if (cart.length === 0) {
        items.innerHTML = `
          <div class="empty-cart reveal-on-scroll is-revealed">
            <svg class="empty-cart__illustration" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
            <h2>${escapeHtml(strings.cartBagEmpty)}</h2>
            <a class="primary-cta" href="${escapeHtml(site.routes.retailCatalog)}" style="margin-top: 12px;">${escapeHtml(strings.browseFlavors)}</a>
          </div>`;
      } else {
        items.innerHTML = cart.map(item => {
          const product = site.getProductById(item.id);
          if (!product) return '';
          const variant = variantForItem(product, item);
          let unitPrice = typeof item.price === 'number' ? item.price : (variant?.price ?? product.price);
          if (item.sellingPlanAllocation?.price && typeof item.sellingPlanAllocation.price === 'number') {
            unitPrice = item.sellingPlanAllocation.price / 100.0;
          } else if (item.sellingPlanId && variant?.sellingPlanAllocations) {
            const alloc = variant.sellingPlanAllocations.find(a => String(a.sellingPlanId) === String(item.sellingPlanId));
            if (alloc && typeof alloc.price === 'number') {
              unitPrice = alloc.price;
            }
          }
          const lineReference = item.key || item.id;
          const lineImage = variant?.featuredMedia?.src || product.image;
          const planLabel = item.sellingPlanName
            || item.sellingPlanAllocation?.selling_plan?.name
            || item.properties?.Subscription
            || item.properties?.subscribe
            || '';
          return `
            <article class="cart-line" data-line="${escapeHtml(lineReference)}">
              <img src="${escapeHtml(lineImage)}" alt="${escapeHtml(interpolate(strings.imageAlt, { product: product.name }))}" width="${Number(variant?.featuredMedia?.width || product.imageWidth) || 800}" height="${Number(variant?.featuredMedia?.height || product.imageHeight) || 800}" />
              <div>
                <h2>${escapeHtml(product.name)}</h2>
                <p>${escapeHtml(product.badge)}${variant?.title ? ` • ${escapeHtml(variant.title)}` : ''}${planLabel ? ` • <span class="cart-line__plan">${escapeHtml(planLabel)}</span>` : ''} • ${escapeHtml(site.money(unitPrice))} ${escapeHtml(strings.each)}</p>
              </div>
              <div class="cart-line__controls">
                <span class="cart-line__price">${escapeHtml(site.money(unitPrice * item.qty))}</span>
                <div class="qty-control">
                  <button type="button" data-cart-minus="${escapeHtml(lineReference)}" aria-label="${escapeHtml(interpolate(strings.decreaseProductQuantity, { product: product.name }))}">−</button>
                  <output>${item.qty}</output>
                  <button type="button" data-cart-plus="${escapeHtml(lineReference)}" aria-label="${escapeHtml(interpolate(strings.increaseProductQuantity, { product: product.name }))}">+</button>
                </div>
                <button class="cart-line__remove" type="button" data-cart-remove="${escapeHtml(lineReference)}">${escapeHtml(strings.remove)}</button>
              </div>
            </article>`;
        }).join('');
      }

      const subtotal = cart.reduce((sum, item) => {
        const product = site.getProductById(item.id);
        if (!product) return sum;
        const variant = variantForItem(product, item);
        let unitPrice = typeof item.price === 'number' ? item.price : (variant?.price ?? product.price);
        if (item.sellingPlanAllocation?.price && typeof item.sellingPlanAllocation.price === 'number') {
          unitPrice = item.sellingPlanAllocation.price / 100.0;
        } else if (item.sellingPlanId && variant?.sellingPlanAllocations) {
          const alloc = variant.sellingPlanAllocations.find(a => String(a.sellingPlanId) === String(item.sellingPlanId));
          if (alloc && typeof alloc.price === 'number') {
            unitPrice = alloc.price;
          }
        }
        return sum + unitPrice * item.qty;
      }, 0);
      const discount = subtotal * discountRate;
      subtotalEl.textContent = site.money(subtotal);
      discountEl.textContent = `-${site.money(discount)}`;
      if (cartPromo) {
        cartPromo.classList.toggle('is-unlocked', progress.unlocked);
        const percent = Math.min(100, (progress.count / site.giftCardPromo.threshold) * 100);

        let promoHTML = '';
        if (progress.unlocked) {
          promoHTML = `
            <strong>${escapeHtml(interpolate(strings.promoUnlocked, { label: site.giftCardPromo.shortLabel }))}</strong>
            <span>${escapeHtml(strings.promoCheckoutMessage)}</span>
          `;
        } else {
          promoHTML = `
            <strong>${site.giftCardPromo.headline}</strong>
            <span>${escapeHtml(interpolate(strings.promoAddBoxes, { count: progress.needed, box: boxLabel(progress.needed) }))}</span>
          `;
        }

        // Append dynamic progress bar markup
        promoHTML += `
          <div class="cart-promo__progress-wrapper">
            <div class="cart-promo__progress-text">
              <span>${escapeHtml(interpolate(strings.boxesProgress, { count: progress.count, threshold: site.giftCardPromo.threshold }))}</span>
              <span>${Math.round(percent)}%</span>
            </div>
            <div class="cart-promo__progress-bar">
              <div class="cart-promo__progress-fill" style="width: ${percent}%;"></div>
            </div>
          </div>
        `;
        cartPromo.innerHTML = promoHTML;
      }
      totalEl.textContent = site.money(Math.max(0, subtotal - discount));
      syncCartCount();
    }

    items.addEventListener('click', async event => {
      const plus = event.target.closest('[data-cart-plus]');
      const minus = event.target.closest('[data-cart-minus]');
      const remove = event.target.closest('[data-cart-remove]');
      if (!plus && !minus && !remove) return;

      try {
        if (remove) {
          const removedItem = await site.removeFromCart(remove.dataset.cartRemove);
          const removedProduct = site.getProductById(removedItem?.id);
          showUndoMessage(removedProduct
            ? interpolate(strings.productRemoved, { product: removedProduct.name })
            : strings.itemRemoved, removedItem);
        } else if (plus) {
          await site.updateCartItemQty(plus.dataset.cartPlus, 1);
          showToast(strings.quantityUpdated);
        } else if (minus) {
          await site.updateCartItemQty(minus.dataset.cartMinus, -1);
          showToast(strings.quantityUpdated);
        }
      } catch (e) {
        showErrorToast(strings.cartUpdateError);
      }
      render();
    });

    if (cartUndo) {
      cartUndo.addEventListener('click', async () => {
        try {
          await site.restoreCartItem(lastRemoved);
          lastRemoved = null;
          if (cartMessage) cartMessage.hidden = true;
          render();
        } catch (e) {
          showErrorToast(strings.cartRestoreError);
        }
      });
    }

    const checkoutBtn = document.querySelector('[data-checkout]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        const cart = site.getCart();
        if (cart.length === 0) {
          showToast(strings.addBeforeCheckout);
          return;
        }
        window.location.href = site.routes.checkout;
      });
    }
    if (confirmationClose) {
      confirmationClose.addEventListener('click', () => { confirmation.hidden = true; });
    }
    window.addEventListener('deli-dele:cart-updated', render);
    render();
  }

  document.addEventListener('DOMContentLoaded', () => {
    syncCartCount();
    initCollection();
    initProduct();
    initCart();
  });

  window.addEventListener('deli-dele:cart-updated', syncCartCount);
})();
